import { useEffect, useRef, useState, type FormEvent } from 'react';
import { labels } from './labels';

/**
 * The console's one dialog. Every guarded write used to spend two native browser
 * popups in a row — `window.confirm` for "are you sure", then `window.prompt` for the reason —
 * each one an OS chrome box that shows the page's origin instead of the page's design, blocks
 * the whole tab, cannot be styled, and drops the answer on a blank submit with no explanation.
 *
 * `ask()` replaces them with a single in-app dialog: the question, what it will act on, and
 * every field the write needs, answered once. The audit name is not a field: the backend records
 * the signed-in user (QA-31). The dialog is a native
 * `<dialog>` opened with `showModal()`, so the focus trap, Escape-to-cancel, the top layer, and the inert background
 * come from the platform rather than from code here.
 *
 * The API is imperative on purpose. The call sites are `async` handlers that already read
 * `if (!confirmed) return;`, and `lib/api.ts`'s `401` retry is not a React component at all, so
 * a promise a handler awaits fits both; a render-prop or context API would not.
 *
 * Returns the answers keyed by field name (`{}` for a confirm-only dialog), or `null` if the
 * operator cancelled — every caller's guard stays `if (!answers) return;`.
 */
export interface AskField {
  name: string;
  label: string;
  /** Blank is refused with an inline message rather than silently aborting the write. */
  required?: boolean;
  /** `number` renders a whole-number field: mono, right-aligned, `min=0`. `select` renders `options`. */
  kind?: 'text' | 'number' | 'select';
  /** The choices of a `select` field. `value` picks the default one. */
  options?: readonly { value: string; label: string }[];
  /** Pre-filled value — the current setting, so the operator edits rather than retypes. */
  value?: string;
}

export interface AskRequest {
  /** The question, in one line. This is the dialog's accessible name. */
  title: string;
  /** What the write will act on, and what it costs. Omitted when the title says all of it. */
  body?: string;
  fields?: readonly AskField[];
  /** A verb, and the same verb as the control that opened this. Never "OK". */
  confirmLabel: string;
  /** The class of the confirm button: `guard` (the default) and `primary` are the filled teal pill (QA-93),
   * `danger` is the filled red pill of a destructive action (QA-108). */
  tone?: 'guard' | 'primary' | 'danger';
  /** `info` draws the body as the information alert. Without it the body is plain text (QA-108). */
  bodyTone?: 'info';
}

const TONE_CLASS = { guard: 'btn--guard', primary: 'btn--primary', danger: 'btn--danger-fill' } as const;

export type AskAnswers = Record<string, string>;

interface Pending {
  id: number;
  request: AskRequest;
  resolve: (answers: AskAnswers | null) => void;
}

let enqueue: ((pending: Pending) => void) | null = null;
let nextId = 0;

export function ask(request: AskRequest): Promise<AskAnswers | null> {
  // No host mounted means no way to ask, and a write that cannot ask must not proceed — the
  // guard is the point of the dialog. Cancelling is the safe answer, never `{}`.
  if (!enqueue) return Promise.resolve(null);
  return new Promise((resolve) => enqueue?.({ id: (nextId += 1), request, resolve }));
}

/**
 * Mounted once, in `AppShell`. A queue rather than a single slot: the background is inert while a
 * dialog is open so an operator cannot open two, but a second `ask()` that still arrives waits its
 * turn, and dropping it would strand the write that asked.
 */
export function AskHost() {
  const [queue, setQueue] = useState<readonly Pending[]>([]);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const current = queue[0];

  useEffect(() => {
    enqueue = (pending) => setQueue((pendings) => [...pendings, pending]);
    return () => {
      enqueue = null;
    };
  }, []);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (current && !dialog.open) dialog.showModal();
    if (!current && dialog.open) dialog.close();
  }, [current]);

  function settle(answers: AskAnswers | null) {
    current?.resolve(answers);
    setQueue((pendings) => pendings.slice(1));
  }

  return (
    <dialog className="dialog" ref={dialogRef} aria-labelledby="dialog-title" onCancel={() => settle(null)}>
      {current && <AskForm key={current.id} request={current.request} onSettle={settle} />}
    </dialog>
  );
}

function AskForm({ request, onSettle }: { request: AskRequest; onSettle: (answers: AskAnswers | null) => void }) {
  const fields = request.fields ?? [];
  const [errors, setErrors] = useState<Record<string, string>>({});

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const answers: AskAnswers = {};
    const found: Record<string, string> = {};

    for (const field of fields) {
      const value = String(data.get(field.name) ?? '').trim();
      const error = validate(field, value);
      if (error) found[field.name] = error;
      answers[field.name] = value;
    }

    setErrors(found);
    if (Object.keys(found).length > 0) return;
    onSettle(answers);
  }

  return (
    <form className="dialog__form" onSubmit={handleSubmit}>
      <h2 className="dialog__title" id="dialog-title">
        {request.title}
      </h2>
      {request.body && (
        <p className={request.bodyTone === 'info' ? 'dialog__body notice notice--info' : 'dialog__body'}>
          {request.body}
        </p>
      )}

      {fields.map((field, index) => {
        const errorId = `dialog-error-${field.name}`;
        const error = errors[field.name];
        return (
          <p className="dialog__field" key={field.name}>
            <label htmlFor={`dialog-${field.name}`}>
              {field.label}
              {!field.required && <span className="dialog__optional">{labels.dialog.optional}</span>}
            </label>
            {field.kind === 'select' ? (
              <select
                id={`dialog-${field.name}`}
                name={field.name}
                defaultValue={field.value ?? ''}
                autoFocus={index === 0}
              >
                {field.options?.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                id={`dialog-${field.name}`}
                name={field.name}
                type={field.kind === 'number' ? 'number' : 'text'}
                {...(field.kind === 'number' ? { min: 0, step: 1, inputMode: 'numeric' as const } : {})}
                defaultValue={field.value ?? ''}
                // The platform focuses the first autofocus target when `showModal()` runs; without
                // a field that is the Cancel button, which is the right default for a bare confirm.
                autoFocus={index === 0}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? errorId : undefined}
                onInput={() =>
                  setErrors((previous) => {
                    const { [field.name]: cleared, ...rest } = previous;
                    return cleared === undefined ? previous : rest;
                  })
                }
              />
            )}
            {error && (
              <span className="dialog__error" id={errorId}>
                {error}
              </span>
            )}
          </p>
        );
      })}

      <div className="dialog__actions">
        {/* Cancel first in the DOM so it takes focus on a confirm-only dialog, and so Escape and
            the visible cheap answer sit in the same place on every dialog in the console. */}
        <button type="button" className="btn btn--text" onClick={() => onSettle(null)}>
          {labels.dialog.cancel}
        </button>
        <button type="submit" className={`btn ${TONE_CLASS[request.tone ?? 'guard']}`}>
          {request.confirmLabel}
        </button>
      </div>
    </form>
  );
}

function validate(field: AskField, value: string): string | null {
  if (field.required && !value) return labels.dialog.required;
  if (field.kind === 'number' && value) {
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed < 0) return labels.dialog.wholeNumber;
  }
  return null;
}
